// eslint-disable-next-line no-restricted-imports
import { AbstractInstanceItem, Creatable, DerivedType, DocumentReferenceBase, Localizable, Typed, TypeOfFields } from 'InstanceFactory';
import { memoize } from '@Common/Core/MemoizeDecorator';
import { helpUrls as helpUrlsType } from '@Common/Core/InstanceFactory/HelpUrls';
import { Errors } from '@Common/Helpers/Errors';

type AbstractInstanceItemAny = AbstractInstanceItem<any> & Partial<DerivedType> & Partial<Localizable<any>>;

export class InstanceFactoryExtension {
    private readonly byTypeNameMap: Map<string, AbstractInstanceItemAny>;

    constructor(private readonly instanceFactory: { [key: string]: AbstractInstanceItemAny }, private readonly helpUrls: typeof helpUrlsType) {
        this.byTypeNameMap = new Map<string, AbstractInstanceItemAny>();
        for (const instance of Object.values(instanceFactory)) {
            this.extendInstanceItem(instance);
            this.byTypeNameMap.set(instance.typeName, instance);
        }
    }

    @memoize()
    public getFields(self: string): TypeOfFields<any> {
        const accum = new Set<string>();
        for (const instance of this.getInheritChain(this.instanceFactory[self])) {
            const types = instance.types ?? {};
            Object.keys(types).forEach(accum.add, accum);
        }
        return [...accum].reduce((p, c) => {
            p[c] = c;
            return p;
        }, {});
    }

    @memoize()
    public getLocalizable(self: string): string[] {
        const accum = new Set<string>();
        for (const instance of this.getInheritChain(this.instanceFactory[self])) {
            const localizable = instance.localizable ?? [];
            localizable.forEach(accum.add, accum);
        }
        return [...accum];
    }

    public byTypeName(typeName: string): AbstractInstanceItemAny {
        return this.byTypeNameMap.get(typeName);
    }

    public addType<T>(
        item: Partial<AbstractInstanceItem<T> & Localizable<T> & Creatable<T> & DerivedType> & Pick<AbstractInstanceItem<T>, 'instanceName' | 'types'>
    ): void {
        const { instanceName } = item;
        _.defaults(item, { typeName: instanceName });
        this.instanceFactory[instanceName] = item as AbstractInstanceItemAny;
        this.byTypeNameMap.set(instanceName, item as AbstractInstanceItemAny);
        this.extendInstanceItem(item as AbstractInstanceItemAny);
    }

    public removeType(instanceName: string): void {
        delete this.instanceFactory[instanceName];
        this.byTypeNameMap.delete(instanceName);
    }

    private extendInstanceItem(desc: AbstractInstanceItemAny): void {
        const self = this;
        _.assign(desc, {
            isInstanceOfType: function (typed?: Typed) {
                if (arguments.length === 1) {
                    return self.canAssign(typed, this);
                }
                if (arguments.length === 0) {
                    return (innerTyped: Typed) => self.canAssign(innerTyped, this);
                }
                Errors.invalidOperation('isInstanceOfType', 'can have 1 or 0 args');
            },
            get fields(): TypeOfFields<any> {
                return self.getFields(desc.instanceName);
            },
            get localizable(): string[] {
                return self.getLocalizable(desc.instanceName);
            }
        });
    }

    @memoize()
    public getHelpUrl(typeName: string, propName: string): string | null {
        let instance = this.byTypeNameMap.get(typeName);
        for (instance of this.getInheritChain(instance)) {
            const helpForInstance = this.helpUrls[instance.instanceName];
            if (helpForInstance && propName in helpForInstance) {
                return helpForInstance[propName];
            }
        }
        return null;
    }

    public canAssign(obj: Typed, instance: AbstractInstanceItemAny): boolean {
        if (obj == null || instance == null) {
            return false;
        }
        const current = this.byTypeName(obj.$type);
        for (const inherited of this.getInheritChain(current)) {
            if (instance === inherited) {
                return true;
            }
        }
        return false;
    }

    private *getInheritChain(current: AbstractInstanceItemAny | null): IterableIterator<AbstractInstanceItemAny> {
        while (current != null) {
            yield current;
            const { inherit = '' } = current;
            if (inherit.startsWith('DocumentReference_')) {
                current = DocumentReferenceBase as AbstractInstanceItemAny;
            } else {
                current = this.instanceFactory[inherit];
            }
        }
    }
}
